import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { adminGetArticle, adminCreateArticle, adminUpdateArticle, formatApiErrorDetail } from "../../lib/api";
import { CATEGORIES } from "../../lib/categories";
import { ArrowLeft, Save } from "lucide-react";
import MetaTags from "../../lib/MetaTags";

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  meta_description: "",
  content: "",
  category: CATEGORIES[0].slug,
  tags: "",
  hero_image: "",
  author: "Editorial Team",
  read_time: 7,
  featured: false,
  faqs: [],
};

export default function ArticleEditor() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin21");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!id || !user) return;
    adminGetArticle(id).then((a) => {
      setForm({
        ...a,
        tags: (a.tags || []).join(", "),
      });
      setLoaded(true);
    }).catch(() => setError("Failed to load article"));
  }, [id, user]);

  const update = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: val });
  };

  const addFaq = () => setForm({ ...form, faqs: [...(form.faqs || []), { question: "", answer: "" }] });
  const removeFaq = (i) => setForm({ ...form, faqs: form.faqs.filter((_, j) => j !== i) });
  const setFaq = (i, k, v) => {
    const arr = [...form.faqs]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, faqs: arr });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const body = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      read_time: parseInt(form.read_time) || 5,
    };
    try {
      if (id) {
        await adminUpdateArticle(id, body);
      } else {
        await adminCreateArticle(body);
      }
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded || authLoading || !user) return <div className="ceg-container py-10 text-sm text-[#5C5C5C]">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      <MetaTags title={id ? "Edit article" : "New article"} />
      <header className="bg-[#121212] text-white">
        <div className="ceg-container py-4 flex items-center justify-between">
          <Link to="/admin" className="text-sm flex items-center gap-2 hover:text-[#FF3823]" data-testid="editor-back">
            <ArrowLeft size={16} /> Back to admin
          </Link>
          <span className="text-xs uppercase tracking-wider">{id ? "Edit article" : "New article"}</span>
        </div>
      </header>

      <form onSubmit={submit} className="ceg-container py-8 grid grid-cols-12 gap-6" data-testid="article-editor">
        {error && (
          <div className="col-span-12 p-3 border-l-4 border-[#FF3823] bg-red-50 text-sm" data-testid="editor-error">{error}</div>
        )}

        <div className="col-span-12 md:col-span-8 space-y-4 bg-white border border-[#121212] p-6">
          <div>
            <label className="ceg-overline block mb-2">Title</label>
            <input required value={form.title} onChange={update("title")} className="w-full border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF] font-['Outfit'] font-bold text-xl" data-testid="editor-title" />
          </div>
          <div>
            <label className="ceg-overline block mb-2">Slug (auto if blank)</label>
            <input value={form.slug} onChange={update("slug")} className="w-full border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF] font-mono text-sm" data-testid="editor-slug" />
          </div>
          <div>
            <label className="ceg-overline block mb-2">Excerpt</label>
            <textarea required value={form.excerpt} onChange={update("excerpt")} rows={3} className="w-full border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF] text-sm resize-none" data-testid="editor-excerpt" />
          </div>
          <div>
            <label className="ceg-overline block mb-2">Meta description (SEO)</label>
            <textarea value={form.meta_description} onChange={update("meta_description")} rows={2} className="w-full border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF] text-sm resize-none" data-testid="editor-meta" />
          </div>
          <div>
            <label className="ceg-overline block mb-2">Content (Markdown)</label>
            <textarea required value={form.content} onChange={update("content")} rows={24} className="w-full border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF] font-mono text-xs leading-relaxed" data-testid="editor-content" />
          </div>

          <div className="border-t border-[#E5E5E5] pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="ceg-overline">FAQs</p>
              <button type="button" onClick={addFaq} className="text-xs uppercase tracking-wider underline" data-testid="editor-add-faq">+ Add FAQ</button>
            </div>
            <div className="space-y-3">
              {(form.faqs || []).map((f, i) => (
                <div key={i} className="border border-[#E5E5E5] p-3">
                  <input value={f.question} onChange={(e) => setFaq(i, "question", e.target.value)} placeholder="Question" className="w-full border border-[#121212] px-2 py-1 outline-none text-sm" data-testid={`faq-q-${i}`} />
                  <textarea value={f.answer} onChange={(e) => setFaq(i, "answer", e.target.value)} placeholder="Answer" rows={3} className="w-full border border-[#121212] px-2 py-1 outline-none text-sm mt-2 resize-none" data-testid={`faq-a-${i}`} />
                  <button type="button" onClick={() => removeFaq(i)} className="text-xs text-[#FF3823] mt-2 uppercase">Remove</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="col-span-12 md:col-span-4 space-y-4">
          <div className="bg-white border border-[#121212] p-5 space-y-4">
            <div>
              <label className="ceg-overline block mb-2">Category</label>
              <select value={form.category} onChange={update("category")} className="w-full border border-[#121212] px-3 py-2 outline-none text-sm" data-testid="editor-category">
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="ceg-overline block mb-2">Tags (comma sep)</label>
              <input value={form.tags} onChange={update("tags")} className="w-full border border-[#121212] px-3 py-2 outline-none text-sm" data-testid="editor-tags" />
            </div>
            <div>
              <label className="ceg-overline block mb-2">Hero image URL</label>
              <input value={form.hero_image} onChange={update("hero_image")} className="w-full border border-[#121212] px-3 py-2 outline-none text-xs" data-testid="editor-image" />
              {form.hero_image && <img src={form.hero_image} alt="" className="mt-2 w-full aspect-video object-cover border border-[#E5E5E5]" />}
            </div>
            <div>
              <label className="ceg-overline block mb-2">Read time (min)</label>
              <input type="number" min={1} value={form.read_time} onChange={update("read_time")} className="w-full border border-[#121212] px-3 py-2 outline-none text-sm" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.featured} onChange={update("featured")} data-testid="editor-featured" />
              <span className="ceg-overline">Mark as featured</span>
            </label>
          </div>

          <button type="submit" disabled={saving} className="ceg-btn-primary w-full disabled:opacity-60 flex items-center justify-center gap-2" data-testid="editor-save">
            <Save size={16} /> {saving ? "Saving..." : (id ? "Update article" : "Publish article")}
          </button>
        </aside>
      </form>
    </div>
  );
}
